import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAttempts, getPlans, calculateMasteryMetrics, saveSettings, getSettings, getAddedVerses, saveAddedVerses } from '../utils/storage';
import { getBookNameById, makeBookAbbrMap } from '../utils/books';
import SingleVerseDialog from './SingleVerseDialog';

const Progress = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [addedVerses, setAddedVerses] = useState([]);
  const { selectedBibleId } = getSettings();

  // Book abbreviation map
  const bookAbbrMap = makeBookAbbrMap();

  useEffect(() => {
    // Load added verses from storage
    setAddedVerses(getAddedVerses());
    calculateProgress();
  }, []);

  // Convert verseId (e.g., "GEN.1.1") to full reference (e.g., "Genesis 1:1")
  const verseIdToReference = (verseId) => {
    const parts = verseId.split('.');
    if (parts.length === 3) {
      const bookId = parts[0];
      const chapter = parts[1];
      const verse = parts[2];
      const bookName = getBookNameById(bookId);
      if (bookName) {
        return `${bookName} ${chapter}:${verse}`;
      }
    }
    return verseId; // Fallback to original if conversion fails
  };

  // Convert full reference (e.g., "Genesis 1:1") to verseId (e.g., "GEN.1.1")
  const referenceToVerseId = (reference) => {
    const parts = reference.trim().split(/\s+/);
    if (parts.length < 2) return '';
    let bookPart = parts[0].toLowerCase();
    let refIndex = 1;
    while (refIndex < parts.length && parts[refIndex] && !parts[refIndex].includes(':')) {
      bookPart += ' ' + parts[refIndex].toLowerCase();
      refIndex++;
    }
    if (refIndex >= parts.length || !parts[refIndex].includes(':')) return '';
    const abbr = bookAbbrMap[bookPart];
    if (!abbr) return '';
    const chapterVerse = parts[refIndex];
    const [chapter, verse] = chapterVerse.split(':');
    if (!chapter || !verse) return '';
    return `${abbr}.${chapter}.${verse}`;
  };

  const handleAddVerse = (reference) => {
    const verseId = referenceToVerseId(reference);
    if (!verseId) return;
    const now = Date.now();
    const updatedAddedVerses = [...addedVerses, { verseId, reference, addedAt: now }];
    setAddedVerses(updatedAddedVerses);
    saveAddedVerses(updatedAddedVerses);
    calculateProgress(updatedAddedVerses); // Pass the updated array directly
  };

  const handleDeleteVerse = (verseId) => {
    if (window.confirm('Are you sure you want to remove this verse from your list?')) {
      const updatedAddedVerses = addedVerses.filter(v => v.verseId !== verseId);
      setAddedVerses(updatedAddedVerses);
      saveAddedVerses(updatedAddedVerses);
      calculateProgress(updatedAddedVerses);
    }
  };

  const calculateProgress = (overrideAddedVerses) => {
    const currentAddedVerses = overrideAddedVerses || addedVerses;
    const attempts = getAttempts();
    const plans = getPlans();

    // Get all verseIds in plans
    const plannedVerses = new Set();
    plans.forEach(plan => {
      plan.verses.forEach(verse => plannedVerses.add(verse));
    });

    // Group attempts by verseId, including both planned and unplanned verses
    const verseAttempts = {};
    attempts.forEach(attempt => {
      if (!verseAttempts[attempt.verseId]) {
        verseAttempts[attempt.verseId] = [];
      }
      verseAttempts[attempt.verseId].push(attempt);
    });

    // For each verse, calculate patent-aligned mastery metrics
    const progress = Object.keys(verseAttempts).map(verseId => {
      const atts = verseAttempts[verseId].sort((a, b) => a.timestamp - b.timestamp);
      const masteryMetrics = calculateMasteryMetrics(atts);
      const averageAccuracy = atts.reduce((sum, attempt) => sum + attempt.accuracy, 0) / atts.length;

      return {
        verseId,
        reference: verseId, // verse reference
        attempts: atts.length,
        // New patent-aligned mastery metrics
        masteryScore: masteryMetrics.score,
        masteryLevel: masteryMetrics.levelLabel,
        masteryLevelColor: masteryMetrics.levelColor,
        isMastered: masteryMetrics.isMastered,
        accuracy: masteryMetrics.accuracy,
        consistency: masteryMetrics.consistency,
        progression: masteryMetrics.progression,
        details: masteryMetrics.details,
        // Keep backward compatibility
        mastery: masteryMetrics.score, // Keep for progress bars
        averageAccuracy: Math.round(averageAccuracy),
        lastAttempt: atts[atts.length - 1],
      };
    });

    // Add addedVerses that don't have attempts yet
    const progressVerses = new Set(progress.map(p => p.verseId));
    currentAddedVerses.forEach(added => {
      if (!progressVerses.has(added.verseId)) {
        progress.push({
          verseId: added.verseId,
          reference: added.reference,
          attempts: 0,
          masteryScore: 0,
          masteryLevel: 'New',
          isMastered: false,
          accuracy: 0,
          consistency: 0,
          progression: 0,
          averageAccuracy: 0,
          lastAttempt: { timestamp: added.addedAt },
        });
      }
    });

    // Sort by most recent lastAttempt (descending)
    progress.sort((a, b) => b.lastAttempt.timestamp - a.lastAttempt.timestamp);

    setProgressData(progress);
  };



  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
      {/* Header */}
      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerHighest',
          border: `1px solid ${theme.palette.outline.variant}`,
        }}
      >
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <MenuBookIcon sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  mb: 1
                }}
              >
                My Verses
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: theme.palette.text.secondary }}
              >
                Track your memorization journey and see your improvement over time
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add Verse Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsDialogOpen(true)}
          size="large"
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: theme.shape.borderRadius * 1.5,
            background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: theme.shadows[2],
            '&:hover': {
              background: `linear-gradient(145deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: theme.shadows[4],
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Add Verse
        </Button>
      </Box>

      {/* Progress Cards */}
      {progressData.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <SchoolIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2, opacity: 0.6 }} />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 500,
              mb: 1
            }}
          >
            No progress data yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, opacity: 0.8 }}
          >
            Start practicing verses to see your progress here
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {progressData.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.verseId}>
              <Card
                sx={{
                  borderRadius: theme.shape.borderRadius * 2,
                  backgroundColor: 'background.surfaceContainerLow',
                  border: `1px solid ${theme.palette.outline.light}`,
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'background.surfaceContainer',
                  }
                }}
              >
                <CardContent sx={{ p: 3, height: '100%', position: 'relative' }}>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: theme.palette.error.main + '20',
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteVerse(item.verseId);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pr: 5 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                      {item.reference}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.masteryLevel}
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Chip
                      label={`Accuracy: ${item.averageAccuracy}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${item.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: item.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${item.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Mastery: ${item.masteryScore}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${item.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Attempts: ${item.attempts}`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: theme.palette.grey[400] + '20',
                        color: theme.palette.grey[700],
                        fontWeight: 600,
                        border: `1px solid ${theme.palette.grey[400]}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: theme.shape.borderRadius }}
                    onClick={() => {
                      const verseReference = verseIdToReference(item.verseId);
                      saveSettings({ ...getSettings(), selectedVerseReference: verseReference });
                      navigate('/memorize');
                    }}
                  >
                    Memorize
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Single Verse Dialog */}
      <SingleVerseDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        selectedBible={selectedBibleId}
        onAddVerse={handleAddVerse}
      />
    </Box>
  );
};

export default Progress;
